<script setup lang="ts">
/**
 * Structured view of a survey's raw payload. Mirrors the legacy
 * `app/projects/data/modal.html` — sections in the same order, with the same
 * `snakeToHuman` and `getHeaders` helpers for dynamic per-category sample
 * tables.
 *
 * Mounted via `useAppDialog().open(OrderDetailsDialog, { data: surveyData })`
 * — read the data through Vue's `inject('dialogRef')`, which is provided by
 * PrimeVue's DialogService at mount time.
 */
import { computed, inject, type Ref } from 'vue';
import { dedupeWords, formatCollectedDateTime, naturalCompare } from '@/utils/filters';

interface DialogRefValue {
  close: (data?: unknown) => void;
  data?: unknown;
}
const dialogRef = inject<Ref<DialogRefValue | undefined>>('dialogRef');

interface CompanyInformation {
  name?: string;
  city?: string;
  state?: string;
}
interface LabInformation {
  lab_id?: string;
  name?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  phone?: string;
  email?: string;
}
interface ProjectSite {
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  building_type?: string;
}
interface ProjectOverview {
  temperature?: number | string;
  temperature_units?: string;
  humidity?: number | string;
}
interface Source {
  type?: string;
  version?: string;
  source?: string;
}
interface Signature {
  signature?: string;
}
interface Photo {
  url: string;
}
type Sample = Record<string, unknown>;
interface Material {
  ha_group?: string;
  material?: string;
  color?: string;
  details?: string;
  photo?: Photo[];
}
interface MaterialLocation {
  ha_group?: string;
  location?: string;
  quantity?: number | string;
  units?: string;
  condition?: string;
}
interface SurveyDetails {
  reservation_code?: string;
  source?: Source;
  company_information?: CompanyInformation;
  lab_information?: LabInformation;
  project_site?: ProjectSite;
  project_overview?: ProjectOverview;
  signature?: Signature;
  signature_date?: string;
  samples?: Record<string, Sample[]>;
  materials?: Material[];
  material_locations?: MaterialLocation[];
}

const viewData = computed<SurveyDetails>(
  () => (dialogRef?.value?.data as SurveyDetails | undefined) ?? {},
);

/** snake_case_field → "Snake Case Field". Mirrors legacy modal.js. */
function snakeToHuman(str: string): string {
  return str
    .split('_')
    .map((w) => (w.length ? w.charAt(0).toUpperCase() + w.slice(1) : ''))
    .join(' ');
}

/** Columns the legacy modal floats to the front when they're present. */
const PREFERRED_HEADERS = [
  'customer_sample_id',
  'ha_group',
  'category',
  'matrix',
  'test_method',
  'turnaround_time',
];

/** Union of keys across every sample row (Angular's `$$`-prefixed keys are
 *  irrelevant in Vue, but we still skip anything starting with `$$` to be
 *  defensive). Preferred headers float to the front in legacy order. */
function getHeaders(samples: Sample[] | undefined): string[] {
  if (!samples) return [];
  const seen: string[] = [];
  for (const row of samples) {
    for (const key of Object.keys(row)) {
      if (!key.startsWith('$$') && !seen.includes(key)) seen.push(key);
    }
  }
  const front = PREFERRED_HEADERS.filter((h) => seen.includes(h));
  const rest = seen.filter((h) => !front.includes(h));
  return [...front, ...rest];
}

function isPhotoArray(v: unknown): v is Photo[] {
  return Array.isArray(v) && v.every((p) => typeof p === 'object' && p !== null && 'url' in p);
}

/** Per-header cell formatter. Scrubs `test_method` and `matrix` strings of
 *  duplicate category-prefix words. Handles both space-separated dupes
 *  ("MICRO Micro Tape" → "Micro Tape") and underscore-glued dupes
 *  ("MICRO_micro Air" → "Micro Air"). The collected timestamp is shown in a
 *  friendly form without shifting it into the viewer's timezone. */
function formatCell(header: string, value: unknown): unknown {
  if (header === 'test_method' || header === 'matrix') return dedupeWords(value);
  if (header === 'datetime_collected' || header === 'datetimeCollected') {
    return formatCollectedDateTime(value);
  }
  return value ?? '';
}

/** Natural-sort a sample category's rows the way the legacy server's
 *  `Naturally.sort_by` in `Laboratory::Import.generate` did: by
 *  `customer_sample_id`, but falling back to `[ha_group, customer_sample_id]`
 *  for asbestos-bulk style data (any row in the group has an `ha_group`). */
function sortSamples(samples: Sample[] | undefined): Sample[] {
  if (!samples || samples.length < 2) return samples ?? [];
  const hasHaGroup = samples.some((s) => {
    const g = s.ha_group;
    return g != null && String(g).length > 0;
  });
  return [...samples].sort((a, b) => {
    if (hasHaGroup) {
      const c = naturalCompare(a.ha_group, b.ha_group);
      if (c !== 0) return c;
    }
    return naturalCompare(a.customer_sample_id, b.customer_sample_id);
  });
}

/** Categories ordered by the natural-min sample id across all samples in the
 *  category — so "Micro Air" with samples 1–3 displays above "Micro Tape"
 *  with sample 4, even though the server returned the buckets in arbitrary
 *  order. Category headings are also scrubbed of duplicate-prefix words
 *  ("MICRO_micro Air" → "Micro Air"). */
interface CategorySection {
  key: string;
  display: string;
  samples: Sample[];
}
const sampleCategories = computed<CategorySection[]>(() => {
  const map = viewData.value.samples ?? {};
  const sections: CategorySection[] = Object.entries(map).map(([key, samples]) => ({
    key,
    display: String(dedupeWords(key) ?? key),
    samples: sortSamples(samples),
  }));
  sections.sort((a, b) => {
    const aFirst = a.samples[0]?.customer_sample_id;
    const bFirst = b.samples[0]?.customer_sample_id;
    return naturalCompare(aFirst, bFirst);
  });
  return sections;
});

/** Materials ordered by HA Group (natural sort). Ties keep source order
 *  (Array.prototype.sort is stable). */
const sortedMaterials = computed<Material[]>(() => {
  const list = viewData.value.materials ?? [];
  if (list.length < 2) return list;
  return [...list].sort((a, b) => naturalCompare(a.ha_group, b.ha_group));
});

/** Material locations ordered by HA Group, with fully-duplicate rows collapsed.
 *  The payload repeats a location once per referencing sample, so identical
 *  (HA Group, Location, Qty, Units, Condition) rows would otherwise show up
 *  several times — we keep each distinct record once, then sort by HA Group. */
const sortedMaterialLocations = computed<MaterialLocation[]>(() => {
  const list = viewData.value.material_locations ?? [];
  const seen = new Set<string>();
  const unique: MaterialLocation[] = [];
  for (const ml of list) {
    const key = JSON.stringify([
      String(ml.ha_group ?? ''),
      String(ml.location ?? ''),
      String(ml.quantity ?? ''),
      String(ml.units ?? ''),
      String(ml.condition ?? ''),
    ]);
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(ml);
  }
  return unique.sort((a, b) => naturalCompare(a.ha_group, b.ha_group));
});
</script>

<template>
  <div class="order-details">
    <h1 v-if="viewData.reservation_code" class="order-details__code">
      {{ viewData.reservation_code }}
    </h1>

    <section v-if="viewData.source">
      <h2>Source</h2>
      <table class="order-details__kv">
        <tbody>
          <tr>
            <td>Type</td>
            <td>{{ viewData.source.type ?? '—' }}</td>
          </tr>
          <tr>
            <td>Version</td>
            <td>{{ viewData.source.version ?? '—' }}</td>
          </tr>
          <tr>
            <td>Source</td>
            <td>{{ viewData.source.source ?? '—' }}</td>
          </tr>
        </tbody>
      </table>
    </section>

    <section v-if="viewData.company_information">
      <h2>Company Information</h2>
      <table class="order-details__kv">
        <tbody>
          <tr>
            <td>Name</td>
            <td>{{ viewData.company_information.name ?? '—' }}</td>
          </tr>
          <tr>
            <td>City</td>
            <td>{{ viewData.company_information.city ?? '—' }}</td>
          </tr>
          <tr>
            <td>State</td>
            <td>{{ viewData.company_information.state ?? '—' }}</td>
          </tr>
        </tbody>
      </table>
    </section>

    <section v-if="viewData.lab_information">
      <h2>Lab Information</h2>
      <table class="order-details__kv">
        <tbody>
          <tr>
            <td>Lab ID</td>
            <td>{{ viewData.lab_information.lab_id ?? '—' }}</td>
          </tr>
          <tr>
            <td>Name</td>
            <td>{{ viewData.lab_information.name ?? '—' }}</td>
          </tr>
          <tr>
            <td>Address</td>
            <td>{{ viewData.lab_information.address ?? '—' }}</td>
          </tr>
          <tr>
            <td>City</td>
            <td>{{ viewData.lab_information.city ?? '—' }}</td>
          </tr>
          <tr>
            <td>State</td>
            <td>{{ viewData.lab_information.state ?? '—' }}</td>
          </tr>
          <tr>
            <td>Zip</td>
            <td>{{ viewData.lab_information.zip ?? '—' }}</td>
          </tr>
          <tr>
            <td>Phone</td>
            <td>{{ viewData.lab_information.phone ?? '—' }}</td>
          </tr>
          <tr>
            <td>Email</td>
            <td>{{ viewData.lab_information.email ?? '—' }}</td>
          </tr>
        </tbody>
      </table>
    </section>

    <section v-if="viewData.project_site">
      <h2>Project Site</h2>
      <table class="order-details__kv">
        <tbody>
          <tr>
            <td>Address</td>
            <td>{{ viewData.project_site.address ?? '—' }}</td>
          </tr>
          <tr>
            <td>City</td>
            <td>{{ viewData.project_site.city ?? '—' }}</td>
          </tr>
          <tr>
            <td>State</td>
            <td>{{ viewData.project_site.state ?? '—' }}</td>
          </tr>
          <tr>
            <td>Zip</td>
            <td>{{ viewData.project_site.zip ?? '—' }}</td>
          </tr>
          <tr>
            <td>Country</td>
            <td>{{ viewData.project_site.country ?? '—' }}</td>
          </tr>
          <tr>
            <td>Building Type</td>
            <td>{{ viewData.project_site.building_type ?? '—' }}</td>
          </tr>
          <tr v-if="viewData.project_overview?.temperature">
            <td>Temperature</td>
            <td>
              {{ viewData.project_overview.temperature }}
              {{ viewData.project_overview.temperature_units ?? '' }}
            </td>
          </tr>
          <tr v-if="viewData.project_overview?.humidity">
            <td>Humidity</td>
            <td>{{ viewData.project_overview.humidity }}</td>
          </tr>
        </tbody>
      </table>
    </section>

    <section v-if="viewData.signature">
      <h2>Signature</h2>
      <table class="order-details__kv">
        <tbody>
          <tr>
            <td>Signature</td>
            <td>
              <img
                v-if="viewData.signature.signature"
                :src="viewData.signature.signature"
                class="order-details__signature"
                alt="Signature"
              />
              <span v-else>—</span>
            </td>
          </tr>
          <tr>
            <td>Date</td>
            <td>{{ viewData.signature_date ?? '—' }}</td>
          </tr>
        </tbody>
      </table>
    </section>

    <section
      v-for="section in sampleCategories"
      :key="section.key"
      class="order-details__samples"
    >
      <h3>{{ section.display }}</h3>
      <div class="order-details__scroll">
        <table class="order-details__table">
          <thead>
            <tr>
              <th v-for="header in getHeaders(section.samples)" :key="header">
                {{ snakeToHuman(header) }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(sample, idx) in section.samples" :key="idx">
              <td v-for="header in getHeaders(section.samples)" :key="header">
                <template v-if="header === 'photo' && isPhotoArray(sample[header])">
                  <img
                    v-for="(p, pi) in sample[header] as Photo[]"
                    :key="pi"
                    :src="p.url"
                    class="order-details__photo"
                    alt=""
                  />
                </template>
                <template v-else>{{ formatCell(header, sample[header]) }}</template>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <section v-if="viewData.materials?.length" class="order-details__samples">
      <h3>Materials</h3>
      <div class="order-details__scroll">
        <table class="order-details__table">
          <thead>
            <tr>
              <th>HA Group</th>
              <th>Material</th>
              <th>Color</th>
              <th>Details</th>
              <th>Photo</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(m, idx) in sortedMaterials" :key="idx">
              <td>{{ m.ha_group ?? '—' }}</td>
              <td>{{ m.material ?? '—' }}</td>
              <td>{{ m.color ?? '—' }}</td>
              <td>{{ m.details ?? '—' }}</td>
              <td>
                <img
                  v-for="(p, pi) in m.photo ?? []"
                  :key="pi"
                  :src="p.url"
                  class="order-details__photo"
                  alt=""
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <section v-if="viewData.material_locations?.length" class="order-details__samples">
      <h3>Material Locations</h3>
      <div class="order-details__scroll">
        <table class="order-details__table">
          <thead>
            <tr>
              <th>HA Group</th>
              <th>Location</th>
              <th>Qty</th>
              <th>Units</th>
              <th>Condition</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(ml, idx) in sortedMaterialLocations" :key="idx">
              <td>{{ ml.ha_group ?? '—' }}</td>
              <td>{{ ml.location ?? '—' }}</td>
              <td>{{ ml.quantity ?? '—' }}</td>
              <td>{{ ml.units ?? '—' }}</td>
              <td>{{ ml.condition ?? '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

  </div>
</template>

<style scoped>
.order-details {
  padding: 0.5rem 0.25rem;
  color: #1f2937;
}
.order-details__code {
  margin: 0 0 1rem;
  font-size: 1.5rem;
}
.order-details section {
  margin-bottom: 1.5rem;
}
.order-details h2 {
  margin: 0 0 0.5rem;
  font-size: 1.125rem;
  color: #374151;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 0.25rem;
}
.order-details h3 {
  margin: 0 0 0.5rem;
  font-size: 1rem;
  /* Match h2 + table thead — section headings stay in the same dark gray as
   * column titles so categories don't read as accent/link text. */
  color: #374151;
}
.order-details__kv,
.order-details__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}
.order-details__kv td {
  padding: 0.375rem 0.5rem;
  border-bottom: 1px solid #f3f4f6;
  vertical-align: top;
}
.order-details__kv td:first-child {
  width: 25%;
  font-weight: 600;
  color: #4b5563;
  background: #f9fafb;
}
.order-details__scroll {
  overflow-x: auto;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
}
.order-details__table th,
.order-details__table td {
  padding: 0.375rem 0.625rem;
  border-bottom: 1px solid #f3f4f6;
  text-align: left;
  vertical-align: top;
  font-size: 0.8125rem;
}
.order-details__table thead th {
  background: #f9fafb;
  font-weight: 600;
  color: #374151;
  white-space: nowrap;
}
.order-details__signature {
  max-width: 250px;
  display: block;
}
.order-details__photo {
  max-width: 250px;
  display: inline-block;
  margin: 0 0.5rem 0.5rem 0;
}
</style>
